/**
 * Manual Task Service - Handles API interactions for manual task instances
 */

import { get, post } from '../utils/apiUtils';

// Configurable SLA timeout in hours (default: 72 hours)
export const DEFAULT_SLA_HOURS = 72;

/**
 * Search manual task instances by filter criteria
 * @param {Object} filter - Search filter (e.g., { assigned_user_email: "user@example.com" })
 * @returns {Promise<Array>} Array of manual task instances
 */
export const searchManualTasks = async (filter = {}) => {
  try {
    const response = await post('/api/v1/mms_ai_workflows/workflow/manual_task_instance/search', filter);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error searching manual tasks:', error);
    throw error;
  }
};

const MANUAL_TASK_BASE_URL = '/api/v1/mms_ai_workflows/workflow/manual_task_instance';

// Header metrics helpers
export const fetchWorkbenchMetrics = async () => {
  try {
    const [
      totalCases,
      overallTatHours,
      nearSLA,
      highRisk,
      slaPercent,
    ] = await Promise.all([
      get(`${MANUAL_TASK_BASE_URL}/metrics/totalCases`),
      get(`${MANUAL_TASK_BASE_URL}/countOverallTATinHours`),
      get(`${MANUAL_TASK_BASE_URL}/metrics/nearSLA`),
      get(`${MANUAL_TASK_BASE_URL}/metrics/highRisk`),
      get(`${MANUAL_TASK_BASE_URL}/metrics/slaPercent`),
    ]);

    return {
      totalCases: typeof totalCases === 'number' ? totalCases : 0,
      overallTatHours: typeof overallTatHours === 'number' ? overallTatHours : 0,
      nearSLA: typeof nearSLA === 'number' ? nearSLA : 0,
      highRisk: typeof highRisk === 'number' ? highRisk : 0,
      slaPercent: typeof slaPercent === 'number' ? slaPercent : 0,
    };
  } catch (error) {
    console.error('Error fetching workbench metrics:', error);
    throw error;
  }
};

// Lane helpers (Kanban columns)
export const fetchCriticalTasksLane = async (filters = {}, queryParams = '') => {
  try {
    const url = queryParams 
      ? `${MANUAL_TASK_BASE_URL}/lanes/criticalTasks?${queryParams}`
      : `${MANUAL_TASK_BASE_URL}/lanes/criticalTasks`;
    const response = await post(url, filters);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error fetching Critical Tasks lane:', error);
    throw error;
  }
};

export const fetchNeedAttentionLane = async (filters = {}, queryParams = '') => {
  try {
    const url = queryParams 
      ? `${MANUAL_TASK_BASE_URL}/lanes/needAttention?${queryParams}`
      : `${MANUAL_TASK_BASE_URL}/lanes/needAttention`;
    const response = await post(url, filters);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error fetching Need Attention lane:', error);
    throw error;
  }
};

export const fetchPendingTasksLane = async (filters = {}, queryParams = '') => {
  try {
    const url = queryParams 
      ? `${MANUAL_TASK_BASE_URL}/lanes/pendingTasks?${queryParams}`
      : `${MANUAL_TASK_BASE_URL}/lanes/pendingTasks`;
    const response = await post(url, filters);
    return Array.isArray(response) ? response : [];
  } catch (error) {
    console.error('Error fetching Pending Tasks lane:', error);
    throw error;
  }
};

/**
 * Calculate time remaining until SLA breach
 * @param {string} createdAt - ISO timestamp when task was created
 * @param {number} slaMinutes - SLA timeout in minutes (optional)
 * @param {number} defaultSlaHours - Default SLA in hours if slaMinutes not provided
 * @returns {Object} { hoursRemaining, isBreached, isCritical, dueDate }
 */
export const calculateSLAStatus = (createdAt, slaMinutes = null, defaultSlaHours = DEFAULT_SLA_HOURS) => {
  const createdDate = new Date(createdAt);
  const now = new Date();
  
  // Calculate SLA deadline
  const slaHours = slaMinutes ? slaMinutes / 60 : defaultSlaHours;
  const slaDeadline = new Date(createdDate.getTime() + slaHours * 60 * 60 * 1000);
  
  // Calculate time remaining
  const timeRemaining = slaDeadline - now;
  const hoursRemaining = timeRemaining / (1000 * 60 * 60);
  
  return {
    hoursRemaining: Math.max(0, hoursRemaining),
    isBreached: timeRemaining < 0,
    isCritical: hoursRemaining <= 24 && hoursRemaining > 0, // Critical if less than 24 hours
    isAttention: hoursRemaining > 24 && hoursRemaining <= 48, // Needs attention if 24-48 hours
    dueDate: slaDeadline,
    slaHours
  };
};

/**
 * Categorize task into Kanban column based on SLA and priority
 * @param {Object} task - Manual task instance
 * @param {number} defaultSlaHours - Default SLA in hours
 * @returns {string} 'critical' | 'attention' | 'pending'
 */
export const categorizeTask = (task, defaultSlaHours = DEFAULT_SLA_HOURS) => {
  // If no priority, sla_minutes, or due_at, put in Pending
  if (!task.priority && !task.sla_minutes && !task.due_at) {
    return 'pending';
  }
  
  // Calculate SLA status
  const slaStatus = calculateSLAStatus(task.created_at, task.sla_minutes, defaultSlaHours);
  
  // Categorize based on SLA status
  if (slaStatus.isBreached || slaStatus.isCritical) {
    return 'critical';
  } else if (slaStatus.isAttention) {
    return 'attention';
  } else {
    return 'pending';
  }
};

/**
 * Format time remaining for display
 * @param {number} hoursRemaining - Hours remaining until SLA breach
 * @returns {string} Formatted string (e.g., "2 Days", "5 hrs", "Overdue")
 */
export const formatTimeRemaining = (hoursRemaining) => {
  if (hoursRemaining <= 0) {
    return 'Overdue';
  } else if (hoursRemaining < 1) {
    const minutes = Math.round(hoursRemaining * 60);
    return `${minutes} mins`;
  } else if (hoursRemaining < 24) {
    return `${Math.round(hoursRemaining)} hrs`;
  } else {
    const days = Math.round(hoursRemaining / 24);
    return `${days} Day${days > 1 ? 's' : ''}`;
  }
};

/**
 * Group tasks by Kanban column
 * @param {Array} tasks - Array of manual task instances
 * @param {number} defaultSlaHours - Default SLA in hours
 * @returns {Object} { critical: [], attention: [], pending: [] }
 */
export const groupTasksByColumn = (tasks, defaultSlaHours = DEFAULT_SLA_HOURS) => {
  const grouped = {
    critical: [],
    attention: [],
    pending: []
  };
  
  tasks.forEach(task => {
    const category = categorizeTask(task, defaultSlaHours);
    grouped[category].push({
      ...task,
      slaStatus: calculateSLAStatus(task.created_at, task.sla_minutes, defaultSlaHours)
    });
  });
  
  // Sort each group by SLA urgency (most urgent first)
  Object.keys(grouped).forEach(key => {
    grouped[key].sort((a, b) => a.slaStatus.hoursRemaining - b.slaStatus.hoursRemaining);
  });
  
  return grouped;
};

/**
 * Extract display data from task
 * @param {Object} task - Manual task instance
 * @returns {Object} Display-friendly task data
 */
export const extractTaskDisplayData = (task) => {
  const workflowVars = task.session_state?.workflow_vars || {};

  const caseId =
    task.input?.caseId ||
    task.output?.caseId ||
    task.response?.caseId ||
    workflowVars.caseId ||
    'N/A';

  const cardTitle =
    task.input?.cardTitle ||
    task.output?.cardTitle ||
    task.response?.cardTitle ||
    'Manual Task';

  const statusLabel =
    task.input?.statusLabel ||
    task.output?.statusLabel ||
    task.response?.statusLabel ||
    '';

  const statusSeverity =
    task.input?.statusSeverity ||
    task.output?.statusSeverity ||
    task.response?.statusSeverity ||
    'info';

  const confidencePercent =
    task.input?.confidencePercent ||
    task.output?.confidencePercent ||
    task.response?.confidencePercent ||
    0;

  // Helper to ensure value is a string, not an object
  const ensureString = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return '';
    return String(value);
  };

  const insuredName = ensureString(workflowVars.insuredName);
  const rawClaimedAmount = ensureString(workflowVars.claimedAmount);
  
  // Detect user's locale from browser
  const userLocale = navigator.language || navigator.userLanguage || 'en-US';
  
  // Map common locales to their currencies (can be extended)
  const localeToCurrency = {
    'en-US': 'USD',
    'en-GB': 'GBP',
    'en-IN': 'INR',
    'en-AU': 'AUD',
    'en-CA': 'CAD',
    'de-DE': 'EUR',
    'fr-FR': 'EUR',
    'es-ES': 'EUR',
    'it-IT': 'EUR',
    'ja-JP': 'JPY',
    'zh-CN': 'CNY',
    'ko-KR': 'KRW',
  };
  
  const claimedAmount = rawClaimedAmount 
    ? new Intl.NumberFormat(userLocale, { 
        minimumFractionDigits: 0,
        maximumFractionDigits: 0 
      }).format(parseFloat(rawClaimedAmount))
    : '';
  const policyOwner = ensureString(workflowVars.policyOwner);
  
  return {
    id: task.id,
    manualTaskId: task.id,
    caseId,
    category: cardTitle,
    statusLabel,
    statusSeverity,
    confidencePercent,
    workflowCode: task.workflow_code,
    stepCode: task.step_code,
    status: task.status,
    createdAt: task.created_at,
    assignedUser: task.assigned_user_email,
    insuredName,
    claimedAmount,
    policyOwner
  };
};

export default {
  searchManualTasks,
  calculateSLAStatus,
  categorizeTask,
  formatTimeRemaining,
  groupTasksByColumn,
  extractTaskDisplayData,
  DEFAULT_SLA_HOURS
};
